/**
 * API Storage Service - Ersetzt SharePoint Storage
 * Kommuniziert mit dem Backend über REST API
 */

import type {
  CompanySettings,
  DayEntry,
  VacationCarryOver,
  Department,
  EmploymentType,
  WageType,
  MonthlyApproval,
  ManagerAssignment,
  CreateUserData,
  AzureUserResult,
  ImportAzureUserData,
  OrgChartUser,
  TeamMemberEntry,
  VacationAdjustment,
  VacationAdjustmentCategory,
  UserTeamStats,
  CompanyVacationRule,
  MfaStatusResponse,
  MfaSetupInitiateResponse,
  MfaAdminUserStatus,
  HistorySegment,
  SettingsSegment,
  EmploymentHistoryRecord,
  CreateHistoryRequest,
  AbsenceType,
  AbsenceRequest,
  CreateAbsenceRequestData,
  NotificationPreference,
} from '../types';
import { storageLogger } from '../utils/logger';
import { AUTH_SESSION_EXPIRED_EVENT } from '../auth/authEvents';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface UserIndexEntry {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  department?: Department;
  employmentType?: EmploymentType | null;
  isHR?: boolean;
  isManager?: boolean;
  employmentStart?: string | null;
  employmentEnd?: string | null;
  excludeFromExport?: boolean;
  personnelNumber?: string | null;
  rv?: boolean;
  hansefit?: boolean;
  contractualHours?: number | null;
  wageType?: WageType | null;
  monthlySalary?: number | null;
  /** true, sobald das Konto mit Azure verknüpft ist (Erst-Login erfolgt). Dann ist die E-Mail gesperrt. */
  azureLinked?: boolean;
  lastActive: string;
}

/**
 * Prüft ob ein JWT abgelaufen ist (Client-seitige Prüfung, kein Verify).
 * Gibt true zurück wenn das Token abgelaufen oder ungültig ist.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!));
    // 60s Puffer, damit nicht exakt am Ablaufzeitpunkt Requests fehlschlagen
    return payload.exp * 1000 < Date.now() + 60_000;
  } catch {
    return true;
  }
}

export class APIStorage {
  private token: string | null = null;

  constructor(token?: string) {
    this.token = token || this.getStoredToken();
  }

  /**
   * Speichere JWT Token im localStorage
   */
  setToken(token: string) {
    this.token = token;
    localStorage.setItem('jwt_token', token);
  }

  /**
   * Hole JWT Token aus localStorage
   */
  private getStoredToken(): string | null {
    return localStorage.getItem('jwt_token');
  }

  /**
   * Prüft ob ein gültiges (nicht abgelaufenes) JWT im Speicher liegt.
   */
  hasValidToken(): boolean {
    const token = this.token || this.getStoredToken();
    if (!token) return false;
    return !isTokenExpired(token);
  }

  /**
   * Dekodiert das JWT-Payload ohne Verifizierung (Client-seitige Nutzung).
   * Gibt null zurück bei ungültigem Token.
   */
  getTokenPayload(): Record<string, unknown> | null {
    const token = this.token || this.getStoredToken();
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split('.')[1]!));
    } catch {
      return null;
    }
  }

  /**
   * Lösche Token (Logout)
   */
  clearToken() {
    this.token = null;
    localStorage.removeItem('jwt_token');
  }

  /**
   * API Request Helper
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    config?: { suppressAuthEvent?: boolean }
  ): Promise<T> {
    const url = `${API_URL}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let errorMessage = `HTTP ${response.status} ${response.statusText}`;
      try {
        const parsed = JSON.parse(text);
        errorMessage = parsed.error || errorMessage;
      } catch {
        if (text) {
          errorMessage += `: ${text.substring(0, 200)}`;
        }
      }

      // Vacation limit exceeded: permanenter Fehler mit strukturierten Daten
      if (response.status === 400) {
        try {
          const parsed400 = JSON.parse(text);
          if (parsed400.error === 'VACATION_LIMIT_EXCEEDED') {
            throw Object.assign(
              new Error(parsed400.message || 'Urlaubslimit überschritten'),
              { code: 'VACATION_LIMIT_EXCEEDED' as const, limit: parsed400.limit as number, requested: parsed400.requested as number }
            );
          }
        } catch (e) {
          if (e instanceof Error && 'code' in e && e.code === 'VACATION_LIMIT_EXCEEDED') throw e;
        }
      }

      // Status-spezifische Behandlung
      if (response.status === 401) {
        storageLogger.warn('Sitzung abgelaufen — Token wird entfernt', { endpoint });
        this.clearToken();
        if (!config?.suppressAuthEvent) {
          window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
        }
      } else if (response.status === 503) {
        storageLogger.error('Service nicht verfügbar', undefined, { endpoint, status: 503 });
        throw new Error('Der Server ist vorübergehend nicht erreichbar. Bitte versuchen Sie es in wenigen Minuten erneut.');
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  // ===== Authentication =====

  /**
   * Login mit Microsoft Access Token
   */
  async login(accessToken: string): Promise<{
    token: string;
    user: {
      id: string;
      name: string;
      email: string;
      department?: Department;
      isHR: boolean;
      isManager: boolean;
      mfaEnabled?: boolean;
    };
    mfaRequired?: boolean;
    entraMfaVerified?: boolean;
  }> {
    const response = await this.request<{
      token: string;
      user: { id: string; name: string; email: string; department?: Department; isHR: boolean; isManager: boolean; mfaEnabled?: boolean };
      mfaRequired?: boolean;
      entraMfaVerified?: boolean;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ accessToken }),
    }, { suppressAuthEvent: true });

    this.setToken(response.token);
    return response;
  }

  async loginSso(ssoToken: string): Promise<{
    token: string;
    user: {
      id: string;
      name: string;
      email: string;
      department?: Department;
      isHR: boolean;
      isManager: boolean;
      mfaEnabled?: boolean;
    };
    mfaRequired?: boolean;
    entraMfaVerified?: boolean;
  }> {
    const response = await this.request<{
      token: string;
      user: { id: string; name: string; email: string; department?: Department; isHR: boolean; isManager: boolean; mfaEnabled?: boolean };
      mfaRequired?: boolean;
      entraMfaVerified?: boolean;
    }>('/auth/login-sso', {
      method: 'POST',
      body: JSON.stringify({ ssoToken }),
    }, { suppressAuthEvent: true });

    this.setToken(response.token);
    return response;
  }

  // ===== Current User =====

  async getMe(): Promise<{ employmentType?: EmploymentType | null; wageType?: WageType | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; employmentStart?: string | null; employmentEnd?: string | null } | null> {
    try {
      return await this.request<{ employmentType?: EmploymentType | null; wageType?: WageType | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; employmentStart?: string | null; employmentEnd?: string | null }>('/users/me');
    } catch {
      return null;
    }
  }

  // ===== Settings =====

  async getSettings(): Promise<CompanySettings | null> {
    try {
      return await this.request<CompanySettings>('/settings');
    } catch (error) {
      console.error('Fehler beim Laden der Settings:', error);
      return null;
    }
  }

  async saveSettings(settings: CompanySettings): Promise<void> {
    await this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getUserSettings(userId: string): Promise<CompanySettings | null> {
    try {
      return await this.request<CompanySettings>(`/settings/${userId}`);
    } catch (error) {
      console.error('Fehler beim Laden der User-Settings:', error);
      return null;
    }
  }

  async saveUserSettings(userId: string, settings: CompanySettings): Promise<void> {
    await this.request(`/settings/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // ===== Entries =====

  async getEntries(year: number): Promise<Record<string, DayEntry> | null> {
    try {
      return await this.request<Record<string, DayEntry>>(`/entries/${year}`);
    } catch (error) {
      console.error('Fehler beim Laden der Einträge:', error);
      return null;
    }
  }

  async saveEntries(year: number, entries: Record<string, DayEntry>): Promise<void> {
    await this.request(`/entries/${year}`, {
      method: 'PUT',
      body: JSON.stringify(entries),
    });
  }

  async getUserEntries(userId: string, year: number): Promise<Record<string, DayEntry> | null> {
    try {
      return await this.request<Record<string, DayEntry>>(`/entries/${userId}/${year}`);
    } catch (error) {
      console.error('Fehler beim Laden der User-Einträge:', error);
      return null;
    }
  }

  async saveUserEntries(
    userId: string,
    year: number,
    entries: Record<string, DayEntry>
  ): Promise<void> {
    await this.request(`/entries/${userId}/${year}`, {
      method: 'PUT',
      body: JSON.stringify(entries),
    });
  }

  async addManagerNote(userId: string, date: string, note: string): Promise<void> {
    await this.request(`/entries/${userId}/${date}/manager-note`, {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    });
  }

  /**
   * Schreibt berechnete Urlaubsstunden nach §11 BUrlG fest (HR-only).
   *
   * Wird unmittelbar vor dem Lohn-Export aufgerufen: bis dahin sind die Werte
   * nur berechnet, nicht gespeichert, und die exportierte CSV liesse sich nicht
   * gegen den Datenbankstand pruefen.
   */
  async finalizeVacationHours(
    userId: string,
    updates: Array<{ date: string; vacationCalculatedHours: number }>
  ): Promise<{ updatedCount: number }> {
    return this.request<{ updatedCount: number }>(`/entries/${userId}/recalc-vacation-hours`, {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  }

  // ===== Carryover =====

  async getCarryOver(year: number): Promise<VacationCarryOver | null> {
    try {
      const response = await this.request<{ days: number }>(`/carryover/${year}`);
      const expirationDate = `${year}-03-31`;
      const today = new Date();
      const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const expirationDateOnly = new Date(year, 2, 31); // März = Index 2
      return {
        days: response.days,
        fromYear: year - 1,
        expirationDate,
        expired: todayDateOnly > expirationDateOnly,
      };
    } catch (error) {
      console.error('Fehler beim Laden des Übertrags:', error);
      return null;
    }
  }

  async saveCarryOver(year: number, carryOver: VacationCarryOver): Promise<void> {
    await this.request(`/carryover/${year}`, {
      method: 'PUT',
      body: JSON.stringify(carryOver),
    });
  }

  async getUserCarryOver(userId: string, year: number): Promise<VacationCarryOver | null> {
    try {
      const endpoint = `/carryover/${userId}/${year}`;
      const response = await this.request<{ days: number }>(endpoint);
      const expirationDate = `${year}-03-31`;
      const today = new Date();
      const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const expirationDateOnly = new Date(year, 2, 31); // März = Index 2
      return {
        days: response.days,
        fromYear: year - 1,
        expirationDate,
        expired: todayDateOnly > expirationDateOnly,
      };
    } catch {
      return null;
    }
  }

  async saveUserCarryOver(
    userId: string,
    year: number,
    carryOver: VacationCarryOver
  ): Promise<void> {
    await this.request(`/carryover/${userId}/${year}`, {
      method: 'PUT',
      body: JSON.stringify(carryOver),
    });
  }

  async autoCalculateCarryover(
    year: number,
    userId?: string,
    force?: boolean
  ): Promise<{ days: number; source: string }> {
    const forceParam = force ? '?force=true' : '';
    const endpoint = userId
      ? `/carryover/${userId}/auto-calculate/${year}${forceParam}`
      : `/carryover/auto-calculate/${year}`;

    return this.request<{ days: number; source: string }>(endpoint, {
      method: 'POST',
    });
  }

  // ===== Users (HR/Manager) =====

  async getAllUsers(): Promise<UserIndexEntry[]> {
    try {
      const users = await this.request<Array<{ id: string; name: string; firstName?: string; lastName?: string; email: string; department?: Department; employmentType?: EmploymentType | null; isHR?: boolean; isManager?: boolean; employmentStart?: string | null; employmentEnd?: string | null; excludeFromExport?: boolean; personnelNumber?: string | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; wageType?: WageType | null; monthlySalary?: number | null; azureLinked?: boolean; updatedAt: string }>>('/users');
      return users.map((u) => ({
        id: u.id,
        name: u.name,
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        email: u.email,
        department: u.department,
        employmentType: u.employmentType ?? null,
        isHR: u.isHR || false,
        isManager: u.isManager || false,
        employmentStart: u.employmentStart ?? null,
        employmentEnd: u.employmentEnd ?? null,
        excludeFromExport: u.excludeFromExport ?? false,
        personnelNumber: u.personnelNumber ?? null,
        rv: u.rv ?? false,
        hansefit: u.hansefit ?? false,
        contractualHours: u.contractualHours ?? null,
        wageType: u.wageType ?? null,
        monthlySalary: u.monthlySalary ?? null,
        azureLinked: u.azureLinked ?? false,
        lastActive: u.updatedAt,
      }));
    } catch (error) {
      console.error('Fehler beim Laden aller Users:', error);
      return [];
    }
  }

  async getUsersByDepartment(department: Department): Promise<UserIndexEntry[]> {
    try {
      const users = await this.request<Array<{ id: string; name: string; firstName?: string; lastName?: string; email: string; department?: Department; employmentType?: EmploymentType | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; updatedAt: string }>>(`/users/department/${department}`);
      return users.map((u) => ({
        id: u.id,
        name: u.name,
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        email: u.email,
        department: u.department,
        employmentType: u.employmentType ?? null,
        rv: u.rv ?? false,
        hansefit: u.hansefit ?? false,
        contractualHours: u.contractualHours ?? null,
        lastActive: u.updatedAt,
      }));
    } catch (error) {
      console.error('Fehler beim Laden der Department-Users:', error);
      return [];
    }
  }

  async getUserData(
    targetUserId: string,
    year: number
  ): Promise<{
    settings: CompanySettings | null;
    entries: Record<string, DayEntry> | null;
    carryOver: VacationCarryOver | null;
  }> {
    const [settings, entries, carryOver] = await Promise.all([
      this.getUserSettings(targetUserId),
      this.getUserEntries(targetUserId, year),
      this.getUserCarryOver(targetUserId, year),
    ]);

    return { settings, entries, carryOver };
  }

  async updateUserDepartment(userId: string, department: Department): Promise<void> {
    await this.request(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ department }),
    });
  }

  async updateUser(userId: string, data: {
    personnelNumber?: string | null;
    department?: Department | null;
    employmentType?: EmploymentType | null;
    employmentStart?: string | null;
    employmentEnd?: string | null;
    excludeFromExport?: boolean;
    rv?: boolean;
    hansefit?: boolean;
    contractualHours?: number | null;
    wageType?: WageType | null;
    monthlySalary?: number | null;
    firstName?: string;
    lastName?: string;
  }): Promise<void> {
    await this.request(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ===== Personnel Number =====

  async updatePersonnelNumber(userId: string, personnelNumber: string | null): Promise<UserIndexEntry> {
    return this.request<UserIndexEntry>(`/users/${userId}/personnel-number`, {
      method: 'PATCH',
      body: JSON.stringify({ personnelNumber }),
    });
  }

  // ===== E-Mail =====

  /**
   * Ändert die E-Mail-Adresse eines Mitarbeiters (nur HR, nur solange nicht Azure-verknüpft).
   * Wirft bei gesperrtem/kollidierendem Konto (siehe Backend: 409 EMAIL_LOCKED_AZURE_LINKED / EMAIL_ALREADY_EXISTS).
   */
  async updateUserEmail(userId: string, email: string): Promise<UserIndexEntry> {
    return this.request<UserIndexEntry>(`/users/${userId}/email`, {
      method: 'PATCH',
      body: JSON.stringify({ email }),
    });
  }

  // ===== Approvals =====

  async getApprovals(year: number, month: number): Promise<MonthlyApproval[]> {
    try {
      return await this.request<MonthlyApproval[]>(`/approvals/${year}/${month}`);
    } catch (error) {
      console.error('Fehler beim Laden der Freigaben:', error);
      return [];
    }
  }

  async setApproval(userId: string, year: number, month: number, data: { approved: boolean; approvalNote?: string | null }): Promise<MonthlyApproval> {
    return this.request<MonthlyApproval>(`/approvals/${userId}/${year}/${month}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== User Create =====

  async createUser(data: CreateUserData): Promise<UserIndexEntry> {
    const user = await this.request<{ id: string; name: string; firstName?: string; lastName?: string; email: string; department?: Department; employmentType?: EmploymentType | null; isHR?: boolean; isManager?: boolean; employmentStart?: string | null; employmentEnd?: string | null; excludeFromExport?: boolean; personnelNumber?: string | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; wageType?: WageType | null; monthlySalary?: number | null; updatedAt: string }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return {
      id: user.id,
      name: user.name,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email,
      department: user.department,
      employmentType: user.employmentType ?? null,
      isHR: user.isHR || false,
      isManager: user.isManager || false,
      employmentStart: user.employmentStart ?? null,
      employmentEnd: user.employmentEnd ?? null,
      excludeFromExport: user.excludeFromExport ?? false,
      personnelNumber: user.personnelNumber ?? null,
      rv: user.rv ?? false,
      hansefit: user.hansefit ?? false,
      contractualHours: user.contractualHours ?? null,
      wageType: user.wageType ?? null,
      monthlySalary: user.monthlySalary ?? null,
      lastActive: user.updatedAt,
    };
  }

  async searchAzureUsers(query: string): Promise<AzureUserResult[]> {
    return this.request<AzureUserResult[]>(`/users/azure-search?query=${encodeURIComponent(query)}`);
  }

  async importAzureUser(data: ImportAzureUserData): Promise<UserIndexEntry> {
    const user = await this.request<{ id: string; name: string; firstName?: string; lastName?: string; email: string; department?: Department; employmentType?: EmploymentType | null; isHR?: boolean; isManager?: boolean; employmentStart?: string | null; employmentEnd?: string | null; excludeFromExport?: boolean; personnelNumber?: string | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; wageType?: WageType | null; monthlySalary?: number | null; updatedAt: string }>('/users/import-azure', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return {
      id: user.id,
      name: user.name,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email,
      department: user.department,
      employmentType: user.employmentType ?? null,
      isHR: user.isHR || false,
      isManager: user.isManager || false,
      employmentStart: user.employmentStart ?? null,
      employmentEnd: user.employmentEnd ?? null,
      excludeFromExport: user.excludeFromExport ?? false,
      personnelNumber: user.personnelNumber ?? null,
      rv: user.rv ?? false,
      hansefit: user.hansefit ?? false,
      contractualHours: user.contractualHours ?? null,
      wageType: user.wageType ?? null,
      monthlySalary: user.monthlySalary ?? null,
      lastActive: user.updatedAt,
    };
  }

  // ===== User Delete =====

  async deleteUser(userId: string): Promise<void> {
    await this.request(`/users/${userId}`, { method: 'DELETE' });
  }

  // ===== Manager Assignments =====

  async getManagerAssignments(userId: string): Promise<ManagerAssignment[]> {
    try {
      return await this.request<ManagerAssignment[]>(`/users/${userId}/managers`);
    } catch (error) {
      console.error('Fehler beim Laden der Manager-Zuordnungen:', error);
      return [];
    }
  }

  async updateManagerAssignments(userId: string, managerIds: string[]): Promise<void> {
    await this.request(`/users/${userId}/managers`, {
      method: 'PUT',
      body: JSON.stringify({ managerIds }),
    });
  }

  // ===== Org Chart (HR) =====

  async getOrgChart(): Promise<{ users: OrgChartUser[] }> {
    return this.request<{ users: OrgChartUser[] }>('/users/org-chart');
  }

  // ===== Vacation Adjustments (HR) =====

  async getVacationAdjustments(userId: string, year: number): Promise<VacationAdjustment[]> {
    try {
      return await this.request<VacationAdjustment[]>(`/vacation-adjustments/${userId}/${year}`);
    } catch (error) {
      console.error('Fehler beim Laden der Urlaubsanpassungen:', error);
      return [];
    }
  }

  async addVacationAdjustment(
    userId: string,
    year: number,
    data: { days: number; category: VacationAdjustmentCategory; reason: string }
  ): Promise<VacationAdjustment> {
    return this.request<VacationAdjustment>(`/vacation-adjustments/${userId}/${year}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteVacationAdjustment(userId: string, adjustmentId: string): Promise<void> {
    await this.request(`/vacation-adjustments/${userId}/${adjustmentId}`, {
      method: 'DELETE',
    });
  }

  // ===== My Team (Manager) =====

  async getMyTeam(): Promise<TeamMemberEntry[]> {
    try {
      const users = await this.request<Array<{ id: string; name: string; firstName?: string; lastName?: string; email: string; department?: Department; employmentType?: EmploymentType | null; isHR?: boolean; isManager?: boolean; employmentStart?: string | null; employmentEnd?: string | null; excludeFromExport?: boolean; personnelNumber?: string | null; rv?: boolean; hansefit?: boolean; contractualHours?: number | null; wageType?: WageType | null; monthlySalary?: number | null; updatedAt: string; depth: number; viaManagerId: string | null; viaManagerName: string | null }>>('/users/my-team');
      return users.map((u) => ({
        id: u.id,
        name: u.name,
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        email: u.email,
        department: u.department,
        employmentType: u.employmentType ?? null,
        isHR: u.isHR || false,
        isManager: u.isManager || false,
        employmentStart: u.employmentStart ?? null,
        employmentEnd: u.employmentEnd ?? null,
        excludeFromExport: u.excludeFromExport ?? false,
        personnelNumber: u.personnelNumber ?? null,
        rv: u.rv ?? false,
        hansefit: u.hansefit ?? false,
        contractualHours: u.contractualHours ?? null,
        wageType: u.wageType ?? null,
        monthlySalary: u.monthlySalary ?? null,
        lastActive: u.updatedAt,
        depth: u.depth ?? 1,
        viaManagerId: u.viaManagerId ?? null,
        viaManagerName: u.viaManagerName ?? null,
      }));
    } catch (error) {
      console.error('Fehler beim Laden des Teams:', error);
      return [];
    }
  }

  async getTeamStats(from: string, to: string): Promise<{ stats: UserTeamStats[]; period: { from: string; to: string } }> {
    return this.request<{ stats: UserTeamStats[]; period: { from: string; to: string } }>(
      `/users/team-stats?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
  }

  // --- Betriebsferien ---

  async getCompanyVacationRule(): Promise<CompanyVacationRule | null> {
    return this.request<CompanyVacationRule | null>('/company-vacation');
  }

  async saveCompanyVacationRule(rule: Omit<CompanyVacationRule, 'id'>): Promise<CompanyVacationRule> {
    return this.request<CompanyVacationRule>('/company-vacation', {
      method: 'PUT',
      body: JSON.stringify(rule),
    });
  }

  // --- Abwesenheitsarten ---

  async getAbsenceTypes(): Promise<AbsenceType[]> {
    return this.request<AbsenceType[]>('/absence-types');
  }

  async getAllAbsenceTypes(): Promise<AbsenceType[]> {
    return this.request<AbsenceType[]>('/absence-types/all');
  }

  async createAbsenceType(data: Omit<AbsenceType, 'id' | 'isSystem' | 'systemKey' | 'active' | 'createdAt' | 'updatedAt'>): Promise<AbsenceType> {
    return this.request<AbsenceType>('/absence-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAbsenceType(id: string, data: Partial<AbsenceType>): Promise<AbsenceType> {
    return this.request<AbsenceType>(`/absence-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAbsenceType(id: string): Promise<AbsenceType> {
    return this.request<AbsenceType>(`/absence-types/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== MFA =====

  async getMfaStatus(): Promise<MfaStatusResponse> {
    return this.request<MfaStatusResponse>('/mfa/status');
  }

  async mfaSetupInitiate(): Promise<MfaSetupInitiateResponse> {
    return this.request<MfaSetupInitiateResponse>('/mfa/setup/initiate', { method: 'POST' });
  }

  async mfaSetupVerifyTotp(deviceId: string, token: string): Promise<{ verified: boolean }> {
    return this.request<{ verified: boolean }>('/mfa/setup/verify-totp', {
      method: 'POST',
      body: JSON.stringify({ deviceId, token }),
    });
  }

  async mfaSetupGenerateCodes(): Promise<{ codes: string[] }> {
    return this.request<{ codes: string[] }>('/mfa/setup/generate-codes', { method: 'POST' });
  }

  async mfaSetupComplete(deviceId: string): Promise<{ success: boolean; token: string }> {
    const result = await this.request<{ success: boolean; token: string }>('/mfa/setup/complete', {
      method: 'POST',
      body: JSON.stringify({ deviceId }),
    });
    // Neuen Token setzen (enthält mfaVerified=true)
    this.setToken(result.token);
    return result;
  }

  async mfaVerifyTotp(token: string): Promise<{ success: boolean; token: string }> {
    const result = await this.request<{ success: boolean; token: string }>('/mfa/verify/totp', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    this.setToken(result.token);
    return result;
  }

  async mfaVerifyRecoveryCode(code: string): Promise<{ success: boolean; token: string; remainingCodes: number }> {
    const result = await this.request<{ success: boolean; token: string; remainingCodes: number }>('/mfa/verify/recovery-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    this.setToken(result.token);
    return result;
  }

  async mfaSendEmailOtp(): Promise<{ sent: boolean; maskedEmail: string; dryRun?: boolean }> {
    return this.request<{ sent: boolean; maskedEmail: string; dryRun?: boolean }>('/mfa/verify/send-email-otp', {
      method: 'POST',
    });
  }

  async mfaVerifyEmailOtp(code: string): Promise<{ success: boolean; token: string }> {
    const result = await this.request<{ success: boolean; token: string }>('/mfa/verify/email-otp', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    this.setToken(result.token);
    return result;
  }

  async mfaSetupVerifyRecoveryEmail(email: string): Promise<{ sent: boolean; maskedEmail: string; dryRun?: boolean }> {
    return this.request<{ sent: boolean; maskedEmail: string; dryRun?: boolean }>('/mfa/setup/verify-recovery-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async mfaSetupConfirmRecoveryEmail(code: string): Promise<{ confirmed: boolean }> {
    return this.request<{ confirmed: boolean }>('/mfa/setup/confirm-recovery-email', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async mfaRegenerateCodes(totpToken: string): Promise<{ codes: string[] }> {
    return this.request<{ codes: string[] }>('/mfa/manage/regenerate-codes', {
      method: 'POST',
      body: JSON.stringify({ token: totpToken }),
    });
  }

  async mfaDisable(totpToken: string): Promise<{ success: boolean; token: string }> {
    const result = await this.request<{ success: boolean; token: string }>('/mfa/manage/disable', {
      method: 'POST',
      body: JSON.stringify({ token: totpToken }),
    });
    this.setToken(result.token);
    return result;
  }

  // MFA Admin

  async mfaAdminOverview(): Promise<{ users: MfaAdminUserStatus[] }> {
    return this.request<{ users: MfaAdminUserStatus[] }>('/mfa/admin/overview');
  }

  async mfaAdminReset(userId: string): Promise<{ success: boolean; graceUntil: string }> {
    return this.request<{ success: boolean; graceUntil: string }>(`/mfa/admin/${userId}/reset`, { method: 'POST' });
  }

  async mfaAdminForce(userId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/mfa/admin/${userId}/force`, { method: 'POST' });
  }

  // ===== Employment History =====

  async getMySettingsSegments(year: number): Promise<SettingsSegment[]> {
    try {
      return await this.request<SettingsSegment[]>(`/users/history/my-settings-segments/${year}`);
    } catch (error) {
      console.error('Fehler beim Laden der Settings-Segmente:', error);
      return [];
    }
  }

  async getUserSettingsSegments(userId: string, year: number): Promise<SettingsSegment[]> {
    try {
      return await this.request<SettingsSegment[]>(`/users/${userId}/history/settings-segments/${year}`);
    } catch (error) {
      console.error('Fehler beim Laden der User-Settings-Segmente:', error);
      return [];
    }
  }

  async getPayrollSegments(from: string, to: string): Promise<Record<string, HistorySegment[]>> {
    return this.request<Record<string, HistorySegment[]>>(
      `/users/history/segments/batch?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
  }

  async getEmploymentHistory(userId: string): Promise<EmploymentHistoryRecord[]> {
    return this.request<EmploymentHistoryRecord[]>(`/users/${userId}/history`);
  }

  async createHistoryRecord(userId: string, data: CreateHistoryRequest): Promise<EmploymentHistoryRecord> {
    return this.request<EmploymentHistoryRecord>(`/users/${userId}/history`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateHistoryRecord(userId: string, historyId: string, data: Partial<CreateHistoryRequest>): Promise<EmploymentHistoryRecord> {
    return this.request<EmploymentHistoryRecord>(`/users/${userId}/history/${historyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteHistoryRecord(userId: string, historyId: string): Promise<void> {
    await this.request(`/users/${userId}/history/${historyId}`, { method: 'DELETE' });
  }

  // ===== Absence Requests =====

  async createAbsenceRequest(data: CreateAbsenceRequestData): Promise<AbsenceRequest> {
    return this.request<AbsenceRequest>('/absence-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyAbsenceRequests(status?: string): Promise<AbsenceRequest[]> {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<AbsenceRequest[]>(`/absence-requests/my${params}`);
  }

  async cancelAbsenceRequest(id: string): Promise<AbsenceRequest> {
    return this.request<AbsenceRequest>(`/absence-requests/my/${id}`, {
      method: 'DELETE',
    });
  }

  async planAbsenceRequest(data: CreateAbsenceRequestData): Promise<AbsenceRequest> {
    return this.request<AbsenceRequest>('/absence-requests/plan', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async submitPlannedRequest(id: string, employeeNote?: string | null): Promise<AbsenceRequest> {
    return this.request<AbsenceRequest>(`/absence-requests/my/${id}/submit`, {
      method: 'PUT',
      body: JSON.stringify(employeeNote === undefined ? {} : { employeeNote }),
    });
  }

  async getPendingAbsenceRequestsCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/absence-requests/pending/count');
  }

  async getPendingAbsenceRequests(): Promise<AbsenceRequest[]> {
    return this.request<AbsenceRequest[]>('/absence-requests/pending');
  }

  async getAllAbsenceRequests(filters?: { status?: string; userId?: string; from?: string; to?: string }): Promise<AbsenceRequest[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.userId) params.set('userId', filters.userId);
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    const qs = params.toString();
    return this.request<AbsenceRequest[]>(`/absence-requests/all${qs ? `?${qs}` : ''}`);
  }

  async approveAbsenceRequest(id: string, reviewerNote?: string): Promise<AbsenceRequest> {
    return this.request<AbsenceRequest>(`/absence-requests/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ reviewerNote: reviewerNote || null }),
    });
  }

  async rejectAbsenceRequest(id: string, reviewerNote: string): Promise<AbsenceRequest> {
    return this.request<AbsenceRequest>(`/absence-requests/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reviewerNote }),
    });
  }

  // ===== Notification Preferences =====

  async getNotificationPreferences(): Promise<{ preferences: NotificationPreference[] }> {
    return this.request<{ preferences: NotificationPreference[] }>('/notification-preferences');
  }

  async updateNotificationPreferences(preferences: NotificationPreference[]): Promise<void> {
    await this.request<void>('/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  }

}
